import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Loader2, MailCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import { AuthShell } from "@/pages/Login";
import { toast } from "sonner";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Şifremi unuttum" subtitle="E-posta adresinize sıfırlama bağlantısı gönderelim">
      {sent ? (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-6 text-center" data-testid="forgot-success">
          <MailCheck className="mx-auto h-10 w-10 text-emerald-600" />
          <p className="mt-3 font-semibold text-slate-800">E-posta gönderildi</p>
          <p className="mt-1 text-sm text-slate-600">Kayıtlı bir hesap varsa şifre sıfırlama bağlantısını e-posta kutunuzda bulabilirsiniz.</p>
          <Link to="/giris" className="mt-4 inline-block text-sm font-semibold text-emerald-700 hover:underline">Girişe dön</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="email">E-posta</Label>
            <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              data-testid="forgot-email" className="focus-visible:ring-emerald-500" />
          </div>
          <Button type="submit" disabled={loading} data-testid="forgot-submit"
            className="w-full bg-emerald-600 font-semibold hover:bg-emerald-700">
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Sıfırlama Bağlantısı Gönder
          </Button>
          <p className="text-center text-sm text-slate-600">
            <Link to="/giris" className="font-semibold text-emerald-700 hover:underline">Girişe dön</Link>
          </p>
        </form>
      )}
    </AuthShell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Şifreniz güncellendi. Giriş yapabilirsiniz.");
      navigate("/giris");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Yeni şifre belirle" subtitle="Hesabınız için yeni bir şifre oluşturun">
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="password">Yeni Şifre</Label>
          <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
            data-testid="reset-password" placeholder="En az 6 karakter" className="focus-visible:ring-emerald-500" />
        </div>
        <Button type="submit" disabled={loading || !token} data-testid="reset-submit"
          className="w-full bg-emerald-600 font-semibold hover:bg-emerald-700">
          {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Şifreyi Güncelle
        </Button>
      </form>
    </AuthShell>
  );
}
